/* `/billing` — the workspace's plan, credits and purchases, proxied to the billing
   service (backend/modules/billing/README.md). Reads are every member's, writes
   an admin's or the owner's.

   In `shared/api/` because three features read it: the Settings pane, the
   sidebar's credits meter and the plan gate's upgrade prices.

   A purchase answers a provider `redirect_url`; the provider's webhook to billing
   is what makes it real, so the page goes there and the summary is re-read when
   the payer comes back. */
import { useEffect, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";
import { settingsKeys } from "./settings";
import { openRazorpayCheckout } from "@/shared/lib/razorpayCheckout";

import type {
  BillingPlan, BillingProvider, CancelAnswer, BillingSummary, ChangePreview, Page, PlanChange, Portal, Redirect, StatementKind, StatementLine, TopupQuote, Topups,
} from "@/shared/types/billing";

/** `POST /runs` refuses a turn with this (402) when the org has no credits left. */
export const OUT_OF_CREDITS = 3200002;
/** Billing did not answer (503): the pane says so instead of its groups. */
export const BILLING_OFFLINE = 3200003;

export const STATEMENT_PAGE = 20;

export const billingKeys = {
  all: ["billing"] as const,
  summary: () => [...billingKeys.all, "summary"] as const,
  plans: () => [...billingKeys.all, "plans"] as const,
  providers: () => [...billingKeys.all, "providers"] as const,
  topups: () => [...billingKeys.all, "topups"] as const,
  quote: (amount: string) => [...billingKeys.all, "quote", amount] as const,
  statement: (kind: StatementKind, offset: number) => [...billingKeys.all, "statement", kind, offset] as const,
  /* Outside `all` on purpose: every write re-reads `all`, and once a change lands billing
     refuses to price that same move again ("already on this plan"). */
  preview: (planId: string) => ["billing-preview", planId] as const,
};

/** Fired with "success" or "cancel" when a purchase ends in a provider checkout over our
    page, so it ends like one the provider sent back to `?billing=` (useCheckoutReturn). */
export const BILLING_RETURN = "billing-return";

/* ------------------------------ endpoints -------------------------------- */

const get = async <T,>(path: string, params?: object): Promise<T> =>
  unwrap(await client.get<Envelope<T>>(path, { params }));
const post = async <T,>(path: string, body: object = {}): Promise<T> =>
  unwrap(await client.post<Envelope<T>>(path, body));

export const getBillingSummary = () => get<BillingSummary>("/billing/summary");
export const getBillingPlans = () => get<BillingPlan[]>("/billing/plans", { cycle: "MONTHLY" });
export const getBillingProviders = () => get<BillingProvider[]>("/billing/providers");
export const getTopups = () => get<Topups>("/billing/topups");
export const getTopupQuote = (amount: string) => get<TopupQuote>("/billing/quote", { amount });
export const getStatement = (kind: StatementKind, offset: number) =>
  get<Page<StatementLine>>("/billing/statement", { kind, limit: STATEMENT_PAGE, offset });
export const previewChange = (planId: string) =>
  get<ChangePreview>("/billing/subscription/change/preview", { plan_id: planId });

export const subscribe = (body: { plan_id: string; provider: string; idempotency_key: string }) =>
  post<Redirect>("/billing/subscription", body);
export const changePlan = (body: { plan_id: string; idempotency_key: string }) =>
  post<PlanChange>("/billing/subscription/change", body);
export const revokeChange = () => post<PlanChange>("/billing/subscription/change/revoke");
export const cancelPlan = () => post<CancelAnswer>("/billing/subscription/cancel");
export const revokeCancel = () => post<CancelAnswer>("/billing/subscription/cancel/revoke");
export const openPortal = () => post<Portal>("/billing/subscription/portal");
/** A pack from `topups`, or a custom `amount` within its bounds: never both. */
export type TopupChoice = { pack: string } | { amount: string };
export const checkout = (body: TopupChoice & { provider: string; idempotency_key: string }) =>
  post<Redirect>("/billing/checkout", body);

/* -------------------------------- hooks ---------------------------------- */

/* Silent: the Billing pane draws every failure in place (billing offline is one
   line instead of its groups), and the sidebar meter drops out whole. A toast on
   every page load while billing is down would say nothing new. */
export const useBillingSummary = () =>
  useQuery({ queryKey: billingKeys.summary(), queryFn: getBillingSummary, meta: { silent: true } });
/* Silent too: the plan gate reads prices from here on every locked page, and with
   billing off or down it simply shows no price; the Billing pane draws the failure. */
export const useBillingPlans = () =>
  useQuery({ queryKey: billingKeys.plans(), queryFn: getBillingPlans, staleTime: 5 * 60_000, meta: { silent: true } });
export const useBillingProviders = () =>
  useQuery({ queryKey: billingKeys.providers(), queryFn: getBillingProviders, staleTime: 5 * 60_000 });
export const useTopups = () => useQuery({ queryKey: billingKeys.topups(), queryFn: getTopups, staleTime: 5 * 60_000 });
/** No placeholder: a quote is only ever shown beside the amount it prices. */
export const useTopupQuote = (amount: string | null) =>
  useQuery({ queryKey: billingKeys.quote(amount ?? ""), queryFn: () => getTopupQuote(amount!), enabled: !!amount });
/** Paging keeps the last page on screen until the next arrives. */
export const useStatement = (kind: StatementKind, offset: number) =>
  useQuery({ queryKey: billingKeys.statement(kind, offset), queryFn: () => getStatement(kind, offset), placeholderData: keepPreviousData });
/** Dropped once the dialog closes, so every opening prices against the plan the org is on now. */
export const useChangePreview = (planId: string | null) =>
  useQuery({ queryKey: billingKeys.preview(planId ?? ""), queryFn: () => previewChange(planId!), enabled: !!planId, gcTime: 0 });

/* Every write re-reads the account whatever the answer, as members' writes do,
   and `/settings` too: the plan every gate reads comes from billing through it.
   One that answers a provider page leaves for it, and stays pending until the
   page is gone: a second click on the way would start a second purchase. Back
   from the provider, a page the browser kept in its cache is usable again. */
function useBillingMutation<V, T>(mutationFn: (vars: V) => Promise<T>, { purchase = false } = {}) {
  const qc = useQueryClient();
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    const back = (e: PageTransitionEvent) => e.persisted && setLeaving(false);
    window.addEventListener("pageshow", back);
    return () => window.removeEventListener("pageshow", back);
  }, []);
  const mutation = useMutation({
    mutationFn,
    onSuccess: async (out) => {
      const { redirect_url: url, checkout } = out as Redirect;
      if (purchase && checkout) {
        // Same ending as a provider page sending the payer back (useCheckoutReturn).
        setLeaving(true);
        // A script that fails to load rejects the mutation: the global error toast says so.
        const paid = await openRazorpayCheckout(checkout).finally(() => setLeaving(false));
        window.dispatchEvent(new CustomEvent(BILLING_RETURN, { detail: paid ? "success" : "cancel" }));
        return;
      }
      if (!url) return;
      setLeaving(true);
      window.location.assign(url);
    },
    onSettled: () =>
      Promise.all([qc.invalidateQueries({ queryKey: billingKeys.all }), qc.invalidateQueries({ queryKey: settingsKeys.all })]),
  });
  return { ...mutation, isPending: mutation.isPending || leaving };
}

export const useSubscribe = () => useBillingMutation(subscribe, { purchase: true });
export const useChangePlan = () => useBillingMutation(changePlan, { purchase: true });
export const useRevokeChange = () => useBillingMutation<void, PlanChange>(revokeChange);
export const useCancelPlan = () => useBillingMutation<void, CancelAnswer>(cancelPlan);
export const useRevokeCancel = () => useBillingMutation<void, CancelAnswer>(revokeCancel);
export const useCheckout = () => useBillingMutation(checkout, { purchase: true });
export const usePortal = () => useBillingMutation<void, Portal>(openPortal);
